const HANGUL_REGEX = /[가-힣]/;
const RAW_KEY_REGEX = /^(q|o|sx|st|stage)-[a-z0-9]/i;

const OPTIONAL_LABEL_BY_LOCALE: Record<string, string> = {
  english: '(optional)',
  en: '(optional)',
  korean: '(선택)',
  ko: '(선택)',
  japanese: '(任意)',
  ja: '(任意)',
  chinese: '(可选)',
  zh: '(可选)',
};

const MISSING_TRANSLATION_LABEL_BY_LOCALE: Record<string, string> = {
  english: '[No translation]',
  en: '[No translation]',
  korean: '[번역 없음]',
  ko: '[번역 없음]',
  japanese: '[翻訳なし]',
  ja: '[翻訳なし]',
  chinese: '[无翻译]',
  zh: '[无翻译]',
  spanish: '[Sin traducción]',
  es: '[Sin traducción]',
  french: '[Pas de traduction]',
  fr: '[Pas de traduction]',
  german: '[Keine Übersetzung]',
  de: '[Keine Übersetzung]',
  portuguese: '[Sem tradução]',
  pt: '[Sem tradução]',
  russian: '[Нет перевода]',
  ru: '[Нет перевода]',
  arabic: '[لا توجد ترجمة]',
  ar: '[لا توجد ترجمة]',
  hindi: '[अनुवाद उपलब्ध नहीं]',
  hi: '[अनुवाद उपलब्ध नहीं]',
  thai: '[ไม่มีคำแปล]',
  th: '[ไม่มีคำแปล]',
  vietnamese: '[Chưa có bản dịch]',
  vi: '[Chưa có bản dịch]',
  italian: '[Nessuna traduzione]',
  it: '[Nessuna traduzione]',
  polish: '[Brak tłumaczenia]',
  pl: '[Brak tłumaczenia]',
  greek: '[Δεν υπάρχει μετάφραση]',
  el: '[Δεν υπάρχει μετάφραση]',
  hebrew: '[אין תרגום]',
  he: '[אין תרגום]',
  persian: '[ترجمه موجود نیست]',
  fa: '[ترجمه موجود نیست]',
  urdu: '[ترجمہ دستیاب نہیں]',
  ur: '[ترجمہ دستیاب نہیں]',
  bengali: '[অনুবাদ নেই]',
  bn: '[অনুবাদ নেই]',
  gujarati: '[અનુવાદ ઉપલબ્ધ નથી]',
  gu: '[અનુવાદ ઉપલબ્ધ નથી]',
  tamil: '[மொழிபெயர்ப்பு இல்லை]',
  ta: '[மொழிபெயர்ப்பு இல்லை]',
  telugu: '[అనువాదం లేదు]',
  te: '[అనువాదం లేదు]',
  punjabi: '[ਅਨੁਵਾਦ ਉਪਲਬਧ ਨਹੀਂ]',
  pa: '[ਅਨੁਵਾਦ ਉਪਲਬਧ ਨਹੀਂ]',
  tagalog: '[Walang salin]',
  tl: '[Walang salin]',
  haitian_creole: '[Pa gen tradiksyon]',
  ht: '[Pa gen tradiksyon]',
  hmong: '[Tsis muaj kev txhais lus]',
  hmn: '[Tsis muaj kev txhais lus]',
  croatian: '[Nema prijevoda]',
  hr: '[Nema prijevoda]',
  armenian: '[No translation]',
  hy: '[No translation]',
  khmer: '[គ្មានការបកប្រែ]',
  km: '[គ្មានការបកប្រែ]',
  yiddish: '[קיין איבערזעצונג]',
  yi: '[קיין איבערזעצונג]',
};

const normalizeLocaleKey = (locale?: string | null) =>
  (locale || 'english').toLowerCase().trim();

const isKoreanLocale = (locale?: string | null) => {
  const localeKey = normalizeLocaleKey(locale);
  return localeKey === 'korean' || localeKey === 'ko';
};

export const getOptionalLabelForSurveyLocale = (locale?: string | null) => {
  const localeKey = normalizeLocaleKey(locale);
  return OPTIONAL_LABEL_BY_LOCALE[localeKey] || OPTIONAL_LABEL_BY_LOCALE.english;
};

export const getMissingTranslationLabelForSurveyLocale = (locale?: string | null) => {
  const localeKey = normalizeLocaleKey(locale);
  return MISSING_TRANSLATION_LABEL_BY_LOCALE[localeKey] || MISSING_TRANSLATION_LABEL_BY_LOCALE.english;
};

interface NormalizeTranslatedLabelParams {
  locale?: string | null;
  label?: string | null;
  fallbackKey?: string | null;
}

export const normalizeTranslatedLabelForSurveyLocale = ({
  locale,
  label,
  fallbackKey,
}: NormalizeTranslatedLabelParams): string => {
  const normalizedLabel = label?.trim() || '';
  const normalizedFallbackKey = fallbackKey?.trim() || '';
  const candidate = normalizedLabel || normalizedFallbackKey;

  if (!candidate) {
    return getMissingTranslationLabelForSurveyLocale(locale);
  }

  // Raw key patterns (e.g. stage-register, q-date-1) should never be shown
  // to users regardless of locale.
  if (RAW_KEY_REGEX.test(candidate)) {
    return getMissingTranslationLabelForSurveyLocale(locale);
  }

  // Korean base locale is allowed to display Korean text directly.
  // For other locales, Hangul fallbacks are treated as missing translations.
  if (isKoreanLocale(locale)) {
    return candidate;
  }

  if (HANGUL_REGEX.test(candidate)) {
    return getMissingTranslationLabelForSurveyLocale(locale);
  }

  return candidate;
};

export const normalizeQuestionLabelForSurveyLocale = ({
  locale,
  label,
}: Omit<NormalizeTranslatedLabelParams, 'fallbackKey'>): string => {
  const normalizedLabel = normalizeTranslatedLabelForSurveyLocale({
    locale,
    label,
    fallbackKey: '',
  });

  if (normalizedLabel === getMissingTranslationLabelForSurveyLocale(locale)) {
    return '';
  }

  return normalizedLabel;
};
